import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toggleEditMode, updateWidgetProperty, updateWidgetActions, updateBulkWidgetProperty } from 'actions/editor';

import { CAROUSEL, CAROUSEL_CARD, CONTENT_CARD, FORM_STEP, GROUP, MENU, PAGE, SECTION_COLUMN } from 'constants/widgetTypes';

import _ from 'lodash';
import cx from 'classnames';

import { EditorContext } from '../../index';
import { Animation, AnimationProperty } from 'components/Editor/components/animations';

import { Property } from '../../components/properties/';
import Actions from '../../components/actions';
import { LiveElementCreationProperty } from '../../components/liveElements';
import { calculateBoundsForElement, getParentContainerOfType, isWidgetHasHotspotChild, getWidgetType, isTopParentIsHotspot } from 'components/Editor/helper/widget';
import { useHotspotContainerSelector, useAllowCreateLiveElement } from 'components/Editor/hooks/useEditorSession';
import { HotspotActionButton, HotspotCreationProperty } from '../../components/hotspot';
import DropShadow from 'components/Editor/components/properties/dropShadow';
import { getDropShadow } from 'services/cssHelper.service';
import { getColorsConfig } from 'services/cssHelper.service';

import styles from './rectangle.module.scss';

const RectangleWidgetComponent = function({ className, style, widgetProps, widgetRef }) {
  const ref = widgetRef;
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;

  const id = _.get(widgetProps, 'uid');
  const type = _.get(widgetProps, 'type');
  const value = _.get(widgetProps, 'content');

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const properties = _.get(widgetProps, `properties.${breakpoint}`, {});
  const defaultProperties = _.get(widgetProps, `properties.desktop`, {});

  const layout = _.get(properties, 'layout');

  var widgetStyle = {
    ...style,
    height: layout.height,
    width: layout.width
  }

  var rectangleStyle = {
    height: layout.height,
    width: layout.width,
    ...getStyles(defaultProperties)
  };

  return (
    <div id={id}
      ref={ref}
      className={cx(className, styles.rectangle)}
      style={widgetStyle}
      data-widget-id={id}
      data-widget-type={type}
      data-selectable={true}
    >
      <Animation widget={widgetProps}>
        <div className={styles.inner} style={rectangleStyle}>
        </div>
      </Animation>
    </div>
  )
};

const rectangleWidgetEquals = function (prevProps, nextProps) {
  const paths = ['className', 'style', 'widgetProps', 'widgetRef'];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
}

const RectangleWidget = React.memo(RectangleWidgetComponent, rectangleWidgetEquals);

const ShapeWidgetDesktopAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState('style')

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);
  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';
  
  const isHotspotContainer = useHotspotContainerSelector(editorSession, props);
  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);

  const template = _.get(props, 'template.widget') != null;

  const id = _.get(props, 'uid');
  const flip = _.get(props, 'properties.desktop.flip', {}) || { horizontal: false, vertical: false };

  const flipShape = (type) => {

    var flipRequest = {
      ...flip,
      [type]: !flip[type],
    }

    dispatch(updateWidgetProperty(editorSession, props.uid, 'desktop', { flip: flipRequest }));
  }

  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });

  const handleEditMode = (view) => {
    setView(view);
    dispatch(toggleEditMode(editorSession, props.uid, true));
  }

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  const handleLiveElement = () => {
    setView('liveElements')
  }

  const handleActionChange = (action) => {
    const actions = action ? [action] : [];
    dispatch(updateWidgetActions(editorSession, props.uid, actions));
  }

  return (
    <>
      <Property.Action className={styles.iconButton} tooltip='Insert Link' onClick={() => changeView('action')}>
        <i className={cx("cleverstory-link")} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip='Customize' onClick={handleEditMode.bind(this, 'style')}>
        <i className={'cleverstory-customize'} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip='Animation' onClick={() => changeView('animation')}>
        <i className={'cleverstory-animations'} />
      </Property.Action>
      {!template && allowLiveElement &&
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={handleLiveElement}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }

      {!template && !isHotspotContainer &&
        <HotspotActionButton onClick={() => changeView('hotspot')} widget={props} />
      }

      {!editMode && view === 'action' &&
        <Actions
          target={id}
          editorSession={editorSession}
          value={_.get(props, 'actions.0')}
          onChange={handleActionChange}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {editMode && view === 'style' &&
        <RectangleStyleEditor
          widgets={[props]}
          onCancel={() => closePopup()}
        />
      }

      {view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }

      {view === 'hotspot' &&
        <HotspotCreationProperty widget={props} onCancel={() => closePopup()} />
      }

      {!editMode && view === 'animation' &&
        <AnimationProperty
          ids={[id]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[props]}
          onCancel={() => {
            closePopup()
          }}
        />
      }
    </>
  );
}

const ShapeWidgetMobileAction = (props) => {
  const dispatch = useDispatch();

  const [view, setView] = useState('style');

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;
  const allowLiveElement = useAllowCreateLiveElement(editorSession, props);

  const isHotspotContainer = useHotspotContainerSelector(editorSession, props);
  const template = _.get(props, 'template.widget') != null;

  const allowBrandkit = useSelector((state) =>
    _.get(state, `${editorSession}.present.entityType`)) !== 'PALETTE_COMPONENT';

  const id = _.get(props, 'uid');
  const editMode = useSelector(state => {
    return _.get(state, `${editorSession}.present.editMode`) === id
  });
  const flip = _.get(props, `properties.mobile.flip`, {}) || { horizontal: false, vertical: false };

  const flipShape = (type) => {

    var flipRequest = {
      ...flip,
      [type]: !flip[type],
    }

    dispatch(updateWidgetProperty(editorSession, props.uid, 'mobile', { flip: flipRequest }));
  }

  const changeView = (view) => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(view);
  }

  const closePopup = () => {
    dispatch(toggleEditMode(editorSession, props.uid, false));
    setView(null);
  }

  return (
    <>
      <Property.Action className={styles.iconButton} tooltip='Animation' onClick={() => changeView('animation')}>
        <i className={'cleverstory-animations'} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip='Flip Vertical' onClick={() => flipShape('vertical')}>
        <i className={'cleverstory-vertical'} />
      </Property.Action>
      <Property.Action className={styles.iconButton} tooltip='Flip Horizontal' onClick={() => flipShape('horizontal')}>
        <i className={'cleverstory-horizontal'} />
      </Property.Action>
      {!template && allowLiveElement &&
        <Property.Action className={styles.iconButton} tooltip='Live Element' onClick={() => changeView('liveElements')}>
          <i className={'cleverstory-live-element'} />
        </Property.Action>
      }
      {!template && !isHotspotContainer &&
        <HotspotActionButton onClick={() => changeView('hotspot')} widget={props} />
      }

      {!editMode && view === 'animation' &&
        <AnimationProperty
          ids={[id]}
          editor={editorSession}
          editorContext={editorContext}
          widgets={[props]}
          onCancel={() => {
            closePopup()
          }}
        />
      }

      {view === 'liveElements' &&
        <LiveElementCreationProperty elements={[props]} onCancel={() => closePopup()} />
      }
      
      {view === 'hotspot' &&
        <HotspotCreationProperty widget={props} onCancel={() => closePopup()} />
      }
    </>
  );
}

RectangleWidget.moveableConfig = (editorSession, widget) => {
  const contentCard = getParentContainerOfType(editorSession, widget.uid, CONTENT_CARD);
  var bounds = null;
  if (contentCard) {
    bounds = calculateBoundsForElement(contentCard);
  }

  var id = widget && widget.uid ? widget.uid : null;
  const group = getParentContainerOfType(editorSession, id, GROUP);

  return {
    draggable: true,
    resizable: true,
    rotatable: true,
    selectable: true,
    cloneable: true,
    deletable: true,
    bounds: bounds,
    renderDirections: ["s", "se", "sw", "e", "w", "n", "ne", "nw"],
    onReparent: (widget, parent) => {
      let hasTopParentIsHotspot = false;
      const parentWidget = getParentContainerOfType(editorSession, widget, CONTENT_CARD);

      const hasHotspotChild = isWidgetHasHotspotChild(editorSession, widget);
      const parentType = getWidgetType(editorSession, parent);

      if (parentType === CAROUSEL_CARD){
        hasTopParentIsHotspot = isTopParentIsHotspot(editorSession, parent, CAROUSEL);
      }

      const denyHostpotReparent = hasHotspotChild && ![PAGE, SECTION_COLUMN, CAROUSEL_CARD, MENU, FORM_STEP].includes(parentType);

      if (parentWidget || group || denyHostpotReparent || (hasHotspotChild && hasTopParentIsHotspot)) {
        return false;
      }

      return true;
    }
  }
}

RectangleWidget.actions = (editor, breakpoint, props) => {
  if (breakpoint === 'desktop') {
    return (
      <ShapeWidgetDesktopAction
        editor={editor}
        breakpoint={breakpoint}
        {...props}
      />
    );
  }

  if (breakpoint === 'mobile') {
    return (
      <ShapeWidgetMobileAction
        editor={editor}
        breakpoint={breakpoint}
        {...props}
      />
    );
  }

  return false;
}

export const RectangleStyleEditor = ({ widgets, ...rest }) => {
  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editorSession = editorContext.editorSession.id;

  const [defaultState, setDefaultState] = useState({
    background: null,
    border: {
      color: null,
      width: null,
      radius:  {
        topLeft: 0,
        topRight: 0,
        bottomLeft: 0,
        bottomRight: 0
      }
    },
    shadow: {
      show: false,
      axis: null,
      color: null
    }
  });

  const widget = widgets[0];
  const id = _.get(widget, 'uid');

  const breakpoint = useSelector(state => _.get(state, `${editorSession}.present.breakpoint`));

  const isBulk = widgets.length > 1;

  const widgetProps = isBulk ? defaultState : _.get(widget, `properties.${breakpoint}`, {});

  const fillColor = widgetProps.background ?? null;
  const shadowProperties= widgetProps.shadow ?? {};

  const border = _.get(widgetProps, 'border');
  const borderColor = _.get(border, 'color', null);
  const borderWidth = _.get(border, 'width', 0);
  const borderRadius = _.get(border, 'radius', {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0
  });

  const handlePropertyChange = (value) => {
    const widgetIds = widgets.map(w => w.uid);

    if(isBulk) {
      setDefaultState((p) => ({
        ...p,
        ...value
      }));
    }
    dispatch(updateBulkWidgetProperty(editorSession, widgetIds, breakpoint, value));
  }

  const handleFillColorChange = (color) => {
    handlePropertyChange({
      background: color
    });
  }

  const handleBorderColorChange = (color) => {
    handlePropertyChange({
      border: {
        ...border,
        color: color
      }
    });
  }

  const handleBorderWidthChange = (value) => {
    handlePropertyChange({
      border: {
        ...border,
        width: value
      }
    });
  }

  const handleBorderRadiusChange = (radius) => {
    handlePropertyChange({
      border: {
        ...border,
        radius: radius
      }
    });
  }

  const handleShadowChange = (field, value) => {
    handlePropertyChange({
      shadow: {
        ...shadowProperties,
        [field]: value
      }
    });
  }

  return (
    <Property.Dialog className={styles.styleDialog}
      width={246}
      target={`#${id}`}
      title={'Customize Shape'}
      onCancel={rest.onCancel}
    >
      <Property.Body>
        <Property.Color title={'Fill Color'} value={fillColor} onChange={handleFillColorChange} />
        <Property.Color title={'Outline Color'} clearable={true} value={borderColor} onChange={handleBorderColorChange} />

        <Property name="Outline Width" >
          <Property.Slider
            min={0}
            max={10}
            step={1}
            input
            value={borderWidth}
            onChange={handleBorderWidthChange}
          />
        </Property>
        <Property name="Corner Radius" className={styles.buttonStyleProperty}>
          <Property.BorderRadius
            value={borderRadius}
            onChange={handleBorderRadiusChange}
          />
        </Property>
        <DropShadow id={id} widget={widget} properties={shadowProperties} handleShadowChange={handleShadowChange} />
      </Property.Body>
    </Property.Dialog>
  );
}

function getStyles(deviceProperties, active) {

  var styles = {};

  if (!deviceProperties) {
    return styles;
  }

  //fill-color
  const fillRgb = _.get(deviceProperties, 'background', null);
  var background = 'inherit';
  if (fillRgb) {
    background = getColorsConfig(fillRgb);
  }

  styles['background'] = background;

  //border
  const border = _.get(deviceProperties, 'border', {});
  const borderStyle = getBorderStyles(border);
  styles = {
    ...styles,
    ...borderStyle
  }

  //DropShadow
  const shadow = _.get(deviceProperties, 'shadow', {});
  const shadowStyle = getDropShadow(shadow);
  styles = {
    ...styles,
    ...shadowStyle
  }

  return styles;
}

export function getFillStyles(value) {
  if (!value) {
    return null;
  }

  const fillRgb = value;

  var fill = 'inherit';
  if (fillRgb) {
    fill = `rgba(${fillRgb.r}, ${fillRgb.g}, ${fillRgb.b}, ${fillRgb.a})`;
  }

  var styles = {
    backgroundColor: fill
  };

  return styles;
}

export function getBorderStyles(value) {
  if (!value) {
    return null;
  }
  var styles = {};
  const topLeft = _.get(value, 'radius.topLeft', null);
  const topRight = _.get(value, 'radius.topRight', null);
  const bottomLeft = _.get(value, 'radius.bottomLeft', null);
  const bottomRight = _.get(value, 'radius.bottomRight', null);

  if (topLeft) {
    styles['borderTopLeftRadius'] = parseInt(topLeft) + 'px';
  }

  if (topRight) {
    styles['borderTopRightRadius'] = parseInt(topRight) + 'px';
  }

  if (bottomLeft) {
    styles['borderBottomLeftRadius'] = parseInt(bottomLeft) + 'px';
  }

  if (bottomRight) {
    styles['borderBottomRightRadius'] = parseInt(bottomRight) + 'px';
  }

  const colorRgb = _.get(value, 'color');

  var color = 'transparent';
  if (colorRgb) {
    color = getColorsConfig(colorRgb);
  }

  styles['borderColor'] = color;

  const borderWidthString = _.get(value, 'width', null);
  var borderWidget = parseInt(borderWidthString) + 'px';

  styles['borderWidth'] = borderWidget;

  return styles;
}

RectangleWidget.processRequest = (widget) => {
  const actions = _.get(widget, 'actions');
  
  const updated = (actions || []).map((action)=>{
    if(action.type == 'asset'){
      const value = _.get(action, 'value.content.id') || _.get(action, 'value', null);
      return {
        ...action,
        value: value
      }
    }
    return action
  })

  return {
    ...widget,
    actions: updated
  }
}

export { RectangleWidget };
